/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Experiments", description = "Split an audience into randomized groups to measure the true, incremental impact of your marketing campaigns.", lastUpdatedDate = new Date(1789603200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Experiments",
        description: "Split an audience into randomized groups to measure the true, incremental impact of your marketing campaigns.",
        lastUpdatedDate: "Thu Sep 17 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Create an experiment",
          "depth": 0,
          "id": "create-an-experiment"
        }, {
          "value": "Configure measurement",
          "depth": 0,
          "id": "configure-measurement"
        }, {
          "value": "Measure results",
          "depth": 0,
          "id": "measure-results"
        }, {
          "value": "Balance groups with stratified sampling",
          "depth": 0,
          "id": "balance-groups-with-stratified-sampling"
        }, {
          "value": "Holdout group logs",
          "depth": 0,
          "id": "holdout-group-logs"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      strong: "strong",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      ol: "ol",
      img: "img",
      code: "code"
    }, _provideComponents(), props.components), {FeatureFlag} = _components;
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(Info, {
        children: [_jsx(_components.strong, {
          children: "Experiments"
        }), " were previously called ", _jsx(_components.strong, {
          children: "Splits"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers and analysts comparing the effectiveness of different campaign strategies"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A saved ", _jsx(_components.a, {
                    href: "/customer-studio/usage",
                    children: "audience"
                  }), "."]
                }), _jsxs("li", {
                  children: ["At least one ", _jsx(_components.a, {
                    href: "/customer-studio/syncs",
                    children: "sync"
                  }), " or campaign ready to launch."]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Experiments split an audience into randomized groups, so you can measure the lift a campaign actually drives over a holdout or an alternative treatment."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "An experiment assigns the members of an audience to distinct, randomized groups. You apply a different treatment to each group — a promotional email to one group and nothing to a holdout, or a different creative to each — and then compare outcomes to see what actually moved the metric."
      }), "\n", _jsx(_components.p, {
        children: "Randomization is what separates a real effect from a coincidence. Without it, you're left guessing:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Correlation"
          }), " — \"Purchases went up after the campaign, but was it the campaign?\""]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Attribution"
          }), " — \"This user converted after seeing the ad, but would they have converted anyway?\""]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Comparing randomized groups isolates the campaign's impact, so you can measure incrementality, test creatives or channels against each other, and confirm whether personalization outperforms a generic experience."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["To branch members inside a journey instead of an audience, use an ", _jsx(_components.strong, {
            children: "A/B\nsplit"
          }), " tile. See ", _jsx(_components.a, {
            href: "/customer-studio/journeys/tiles#ab-split",
            children: "Journey tiles"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "An experiment moves through four stages: split a saved audience into randomized groups, apply a treatment to each group through syncs, choose the metric that defines success, and compare the results across groups once the campaign runs."
      }), "\n", _jsx(_components.h2, {
        id: "create-an-experiment",
        children: "Create an experiment"
      }), "\n", _jsx(_components.p, {
        children: "You build an experiment on the audience you want to test."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open a saved audience and go to the ", _jsx(_components.strong, {
            children: "Experiment"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create experiment"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Split groups"
          }), ", select ", _jsx(_components.strong, {
            children: "Add split group"
          }), " for each group you want, then set each group's name and its share of the audience. Use two groups for a simple A/B test, or three or more for A/B/n tests."]
        }), "\n", _jsxs(_components.li, {
          children: ["To measure incremental lift, make one group a ", _jsx(_components.strong, {
            children: "Holdout group"
          }), ". A holdout receives no treatment and acts as the control you compare the others against."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Syncs"
          }), ", assign each non-holdout group the syncs that deliver its treatment. A group synced to multiple destinations sends all of its members to every destination — the group isn't subdivided between them."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/experiment-split-groups.png",
          alt: "The Experiment tab showing split groups, their assigned syncs, and measurement configuration"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Each member is assigned to a group once, based on its primary key. Changing\nthe group percentages after launch can reassign members and place a member in\nmore than one group. To keep group membership fixed, use an ", _jsx(_components.a, {
            href: "/customer-studio/audience-snapshots",
            children: "audience\nsnapshot"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "configure-measurement",
        children: "Configure measurement"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Measurement configuration"
        }), ", define how Hightouch scores the experiment. You set this on the same ", _jsx(_components.strong, {
          children: "Experiment"
        }), " tab, below the groups."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Primary metric"
          }), " — The event and value that define success, such as the average ", _jsx(_components.code, {
            children: "QUANTITY_PURCHASED"
          }), " on a purchase event. This is the outcome compared across groups."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Secondary metrics"
          }), " — Optional additional outcomes to track alongside the primary metric."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Start date"
          }), " and ", _jsx(_components.strong, {
            children: "End date"
          }), " — The window the experiment measures. An experiment completes at 11:59:59pm on the end date. Leave the end date empty to keep measuring until you set one."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Measurement window"
          }), " — How long after a member enters (or exits) the audience their outcomes still count toward the experiment."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/experiment-measurement-configuration.png",
          alt: "Measurement configuration showing a primary metric, secondary metrics, start and end dates, and the measurement window"
        })
      }), "\n", _jsx(_components.h2, {
        id: "measure-results",
        children: "Measure results"
      }), "\n", _jsxs(_components.p, {
        children: ["Once the audience has at least one sync involving experiment groups, Hightouch generates a matching ", _jsx(_components.strong, {
          children: "Experiment"
        }), ". Select ", _jsx(_components.strong, {
          children: "View results"
        }), " on the Experiment tab to open it. You can also reach every experiment under ", _jsxs(_components.strong, {
          children: ["Intelligence > ", _jsx(_components.a, {
            href: "https://app.hightouch.com/experiments?from=docs",
            children: "Experiments"
          })]
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/experiments/ci-experiments-nav.png",
          alt: "The Experiments list under Intelligence"
        })
      }), "\n", _jsx(_components.p, {
        children: "In the Experiment, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Compare lift across groups."
        }), "\n", _jsx(_components.li, {
          children: "Check statistical significance."
        }), "\n", _jsx(_components.li, {
          children: "Analyze results by primary or secondary metric."
        }), "\n", _jsx(_components.li, {
          children: "Normalize results per member or against a baseline."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For how to read and configure the results view, see ", _jsx(_components.a, {
          href: "/campaign-intelligence/experiments",
          children: "Experiments in Intelligence"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "balance-groups-with-stratified-sampling",
        children: "Balance groups with stratified sampling"
      }), "\n", _jsxs(_components.p, {
        children: ["Simple randomization can produce unbalanced groups when your audience spans distinct segments — for example, different regions or loyalty tiers. ", _jsx(_components.strong, {
          children: "Stratified sampling"
        }), " keeps those segments proportionally represented across groups, so a difference in outcomes reflects the treatment rather than an uneven split."]
      }), "\n", _jsx(Info, {
        children: "Stratified sampling is available only for one-time syncs."
      }), "\n", _jsx(_components.p, {
        children: "To enable it:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["On the audience's ", _jsx(_components.strong, {
            children: "Experiment"
          }), " tab, expand ", _jsx(_components.strong, {
            children: "Advanced configuration"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Turn on ", _jsx(_components.strong, {
            children: "Stratified sampling"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Stratification variables"
          }), ", select one or more columns to balance across (for example, ", _jsx(_components.code, {
            children: "loyalty_tier"
          }), ")."]
        }), "\n", _jsx(_components.li, {
          children: "Save your changes."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/stratified-sampling.png",
          alt: "Stratified sampling enabled with a stratification variable selected"
        })
      }), "\n", _jsx(_components.h2, {
        id: "holdout-group-logs",
        children: "Holdout group logs"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Holdout group logs"
      }), "\n", _jsxs(_components.p, {
        children: ["Holdout group logs record which rows were ", _jsx(_components.strong, {
          children: "excluded"
        }), " from a sync as part of a holdout group, so data teams can run row-level analysis in the warehouse. Marketers should rely on the Experiment results in Customer Studio instead."]
      }), "\n", _jsx(_components.p, {
        children: "Enabling holdout group logs requires:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning sync engine"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["A destination with ", _jsx(_components.strong, {
            children: "write access"
          }), " to your warehouse."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To enable them:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Integrations > Sources"
          }), " and select a source."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Sync logs"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["On the ", _jsx(_components.strong, {
            children: "Warehouse sync logs"
          }), " card, turn on ", _jsx(_components.strong, {
            children: "Audience holdout group logs"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/splits/cs-splits-holdout-feature.png",
          alt: "The Audience holdout group logs toggle on the Warehouse sync logs card"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Once enabled, Hightouch logs excluded rows to the ", _jsx(_components.code, {
          children: "hightouch_audit.audience_holdout"
        }), " table."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_id"
              })
            }), _jsx(_components.td, {
              children: "Sync ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "sync_run_id"
              })
            }), _jsx(_components.td, {
              children: "Sync run ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "model_id"
              })
            }), _jsx(_components.td, {
              children: "Model or audience ID"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "timestamp"
              })
            }), _jsx(_components.td, {
              children: "Timestamp of the sync"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "row_id"
              })
            }), _jsx(_components.td, {
              children: "Primary key value from the model"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "fields"
              })
            }), _jsx(_components.td, {
              children: "JSON snapshot of model data"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "split_group"
              })
            }), _jsx(_components.td, {
              children: "Name of the experiment group"
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/campaign-intelligence/experiments",
            children: "Experiments in Intelligence"
          }), " to configure and interpret the results view in depth."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/syncs",
            children: "Sync audiences"
          }), " to deliver each group's treatment to a destination."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/audience-snapshots",
            children: "Audience snapshots"
          }), " to freeze group membership before you launch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/journeys/tiles#ab-split",
            children: "Journey tiles"
          }), " to run an A/B split inside a journey instead of an audience."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
